import { z } from "zod";

/**
 * Package Template Allowed Rule Schema - กฎการอนุญาตการใช้แพ็กเกจ
 */
export const PackageTemplateRuleSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  classTypeId: z.string(),
  classTypeName: z.string(),
  levelId: z.string(),
  levelName: z.string(),
  createdAt: z.coerce.date(),
});
export type PackageTemplateRuleDTO = z.infer<typeof PackageTemplateRuleSchema>;

/**
 * Package Template Schema - Template แพ็กเกจ
 */
export const PackageTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  totalSessions: z.number(),
  validDays: z.number(),
  price: z.number(),
  description: z.string().nullable(),
  maxSelectableClasses: z.number().int().positive().nullable().optional(),
  status: z.enum(["active", "inactive"]),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  rules: z.array(PackageTemplateRuleSchema).optional(),
});
export type PackageTemplateDTO = z.infer<typeof PackageTemplateSchema>;

/**
 * Create Package Template Schema
 */
export const CreatePackageTemplateSchema = z
  .object({
    name: z.string().min(1, "ຊື່ຕ້ອງບໍ່ວ່າງ"),
    totalSessions: z.number().int().positive("ຈຳນວນຄັ້ງຕ້ອງເປັນຈຳນວນບວກ"),
    validDays: z.number().int().positive("ຈຳນວນວັນຕ້ອງເປັນຈຳນວນບວກ"),
    price: z.number().int().min(0, "ລາຄາຕ້ອງເປັນຈຳນວນບວກ"),
    description: z.string().nullable().optional(),
    maxSelectableClasses: z.number().int().positive().nullable().optional(),
    status: z.enum(["active", "inactive"]).default("active"),
    rules: z
      .array(
        z.object({
          classTypeId: z.string().min(1, "ຕ້ອງເລືອກປະເພດຄລາດ"),
          levelId: z.string().min(1, "ຕ້ອງເລືອກລະດັບ"),
        }),
      )
      .min(1, "ຕ້ອງມີກົດລະບຽບຢ່າງໜ້ອຍ 1 ກົດລະບຽບ"),
  })
  .refine(
    (data) => {
      if (data.maxSelectableClasses == null) return true;
      return (
        data.maxSelectableClasses >= 1 &&
        data.maxSelectableClasses <= data.rules.length
      );
    },
    {
      message:
        "ຈຳນວນຄລາດສ໌ທີ່ເລືອກໄດ້ຕ້ອງຢູ່ລະຫວ່າງ 1 ເຖິງຈຳນວນກົດລະບຽບ",
    },
  );
export type CreatePackageTemplateDTO = z.infer<
  typeof CreatePackageTemplateSchema
>;

/**
 * Update Package Template Schema
 */
export const UpdatePackageTemplateSchema = z
  .object({
    name: z.string().min(1, "ຊື່ຕ້ອງບໍ່ວ່າງ").optional(),
    totalSessions: z
      .number()
      .int()
      .positive("ຈຳນວນຄັ້ງຕ້ອງເປັນຈຳນວນບວກ")
      .optional(),
    validDays: z
      .number()
      .int()
      .positive("ຈຳນວນວັນຕ້ອງເປັນຈຳນວນບວກ")
      .optional(),
    price: z.number().int().min(0, "ລາຄາຕ້ອງເປັນຈຳນວນບວກ").optional(),
    description: z.string().nullable().optional(),
    maxSelectableClasses: z.number().int().positive().nullable().optional(),
    status: z.enum(["active", "inactive"]).optional(),
    rules: z
      .array(
        z.object({
          classTypeId: z.string().min(1, "ຕ້ອງເລືອກປະເພດຄລາດ"),
          levelId: z.string().min(1, "ຕ້ອງເລືອກລະດັບ"),
        }),
      )
      .min(1, "ຕ້ອງມີກົດລະບຽບຢ່າງໜ້ອຍ 1 ກົດລະບຽບ")
      .optional(),
  })
  .refine(
    (data) => {
      const rules = data.rules;
      const max = data.maxSelectableClasses;
      if (max == null || !rules) return true;
      return max >= 1 && max <= rules.length;
    },
    {
      message:
        "ຈຳນວນຄລາດສ໌ທີ່ເລືອກໄດ້ຕ້ອງຢູ່ລະຫວ່າງ 1 ເຖິງຈຳນວນກົດລະບຽບ",
    },
  );
export type UpdatePackageTemplateDTO = z.infer<
  typeof UpdatePackageTemplateSchema
>;
